import type { World } from '@/ecs/World';

import type { EventBus } from '@/core/EventBus';
import type { HexGrid } from '@/hex/HexGrid';
import type { UnitFactory } from '@/units/UnitFactory';
import type { UnitRenderer } from '@/units/UnitRenderer';
import type { AxialCoord } from '@/types/hex.types';
import { Position } from '@/ecs/Component';
import { Stats, Owner, CombatState, UnitMesh, UnitData } from '@/ecs/components/UnitComponents';
import { GameStateManager, GamePhase, GameStateEvents } from './GameStateManager';
import { CombatSystem, CombatEvents } from '@/combat/CombatSystem';

/**
 * Round manager events
 */
export const RoundEvents = {
  PREP_STARTED: 'round:prepStarted',
  MATCH_INTRO: 'round:matchIntro',
  BATTLE_COUNTDOWN: 'round:battleCountdown',
  SURVIVORS_TALLIED: 'round:survivorsTallied',
} as const;

export interface SurvivorsTalliedEvent {
  player1Survivors: number;
  player2Survivors: number;
  player1TotalHealth: number;
  player2TotalHealth: number;
}

/**
 * Manages round lifecycle and coordinates between game state and combat
 */
export class RoundManager {
  private world: World;
  private eventBus: EventBus;
  private hexGrid: HexGrid;
  private gameState: GameStateManager;
  private combatSystem: CombatSystem;

  private autoStartBattle = true;
  private roundEndTimer = 0;
  private isMultiplayer = false;

  // Store initial positions before battle starts
  private initialPositions: Map<string, AxialCoord> = new Map();

  // References for unit manipulation (set after construction)
  private unitFactory: UnitFactory | null = null;
  private unitRenderer: UnitRenderer | null = null;

  constructor(
    world: World,
    eventBus: EventBus,
    hexGrid: HexGrid,
    gameState: GameStateManager,
    combatSystem: CombatSystem
  ) {
    this.world = world;
    this.eventBus = eventBus;
    this.hexGrid = hexGrid;
    this.gameState = gameState;
    this.combatSystem = combatSystem;

    this.setupEventListeners();
  }

  /**
   * Set unit factory and renderer references
   */
  setUnitSystems(unitFactory: UnitFactory, unitRenderer: UnitRenderer): void {
    this.unitFactory = unitFactory;
    this.unitRenderer = unitRenderer;
  }

  /**
   * Set multiplayer mode (in multiplayer, server is authoritative for scores)
   */
  setMultiplayerMode(isMultiplayer: boolean): void {
    this.isMultiplayer = isMultiplayer;
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Listen for combat end
    this.eventBus.on(CombatEvents.COMBAT_ENDED, () => {
      this.onCombatEnded();
    });

    // Listen for phase changes
    this.eventBus.on<{ previousPhase: GamePhase; newPhase: GamePhase }>(
      GameStateEvents.PHASE_CHANGED,
      (data) => {
        this.onPhaseChanged(data.previousPhase, data.newPhase);
      }
    );
  }

  /**
   * Handle phase change
   */
  private onPhaseChanged(_previous: GamePhase, current: GamePhase): void {
    console.log(`[RoundManager] Phase changed to: ${current}`);
    switch (current) {
      case GamePhase.Preparation:
        this.onPrepPhaseStart();
        break;
      case GamePhase.Battle:
        this.onBattlePhaseStart();
        break;
      case GamePhase.RoundEnd:
        this.onRoundEndPhaseStart();
        break;
    }
  }

  /**
   * Called when preparation phase starts
   */
  private onPrepPhaseStart(): void {
    // Reset unit health and combat state
    this.resetUnitsForNewRound();

    // Show match intro on round 1
    if (this.gameState.currentRound === 1) {
      this.eventBus.emit(RoundEvents.MATCH_INTRO, {});
    }

    this.eventBus.emit(RoundEvents.PREP_STARTED, {
      roundNumber: this.gameState.currentRound,
    });
  }

  /**
   * Called when battle phase starts
   */
  private onBattlePhaseStart(): void {
    console.log('[RoundManager] Battle phase starting - initializing combat');

    // Save initial positions before combat
    this.saveInitialPositions();

    // Start combat system
    this.combatSystem.startCombat();
  }

  /**
   * Save all unit positions before battle
   */
  private saveInitialPositions(): void {
    this.initialPositions.clear();

    const units = this.world
      .query(Position, Owner)
      .filter((entity) => {
        const owner = entity.getComponent(Owner);
        return owner && !owner.isOnBench;
      });

    for (const unit of units) {
      const pos = unit.getComponent(Position)!;
      this.initialPositions.set(unit.id, { q: pos.q, r: pos.r });
    }
  }

  /**
   * Called when round end phase starts
   */
  private onRoundEndPhaseStart(): void {
    // Tally survivors
    const survivors = this.tallySurvivors();

    this.eventBus.emit(RoundEvents.SURVIVORS_TALLIED, survivors);

    // Reset round end timer
    this.roundEndTimer = 0;
  }

  /**
   * Called when combat ends
   */
  private onCombatEnded(): void {
    if (this.gameState.phase !== GamePhase.Battle) return;

    // Determine winner
    const winner = this.combatSystem.getWinner();
    // In multiplayer, server is authoritative for scores
    this.gameState.endRound(winner, this.isMultiplayer);
  }

  /**
   * Update round logic (call each frame)
   */
  update(deltaTime: number): void {
    const phase = this.gameState.phase;

    // Update timer
    const timeExpired = this.gameState.updateTimer(deltaTime);

    // Handle time expiration
    if (timeExpired) {
      switch (phase) {
        case GamePhase.Preparation:
          // In multiplayer, server controls phase transitions - don't auto-start locally
          if (this.autoStartBattle && !this.isMultiplayer) {
            this.gameState.startBattle();
          }
          break;
        case GamePhase.Battle:
          // Force end combat due to time
          this.combatSystem.stopCombat();
          const winner = this.combatSystem.getWinner();
          // In multiplayer, server is authoritative for scores
          this.gameState.endRound(winner, this.isMultiplayer);
          break;
      }
    }

    // Handle round end auto-proceed
    if (phase === GamePhase.RoundEnd) {
      this.roundEndTimer += deltaTime;
      const roundEndDelay = this.gameState.getConfig().roundEndDelay;
      if (this.roundEndTimer >= roundEndDelay) {
        this.proceedToNextRound();
      }
    }
  }

  /**
   * Manually start battle (skip remaining prep time)
   */
  startBattleNow(): void {
    if (this.gameState.phase === GamePhase.Preparation) {
      this.gameState.startBattle();
    }
  }

  /**
   * Set whether battle auto-starts when prep timer ends
   */
  setAutoStartBattle(auto: boolean): void {
    this.autoStartBattle = auto;
  }

  /**
   * Reset all units for a new round
   */
  private resetUnitsForNewRound(): void {
    const units = this.world
      .query(Position, Stats, Owner, CombatState)
      .filter((entity) => {
        const owner = entity.getComponent(Owner);
        return owner && !owner.isOnBench;
      });

    for (const unit of units) {
      const pos = unit.getComponent(Position)!;
      const stats = unit.getComponent(Stats)!;
      const combatState = unit.getComponent(CombatState)!;
      const mesh = unit.getComponent(UnitMesh);

      // Restore to initial position if we have one saved
      const initialPos = this.initialPositions.get(unit.id);
      if (initialPos && this.unitFactory) {
        // Clear old tile occupant
        const oldTile = this.hexGrid.getTile({ q: pos.q, r: pos.r });
        if (oldTile && oldTile.occupant === unit.id) {
          oldTile.occupant = null;
        }

        // Update position component
        (pos as { q: number }).q = initialPos.q;
        (pos as { r: number }).r = initialPos.r;

        // Set new tile occupant
        const newTile = this.hexGrid.getTile(initialPos);
        if (newTile) {
          newTile.occupant = unit.id;
        }

        // Update visual position
        if (mesh && this.unitRenderer) {
          this.unitRenderer.updatePosition(mesh.meshId, initialPos);
        }
      }

      // Reset health to max
      (stats as { currentHealth: number }).currentHealth = stats.maxHealth;

      // Reset combat state
      (combatState as { targetEntityId: string | null }).targetEntityId = null;
      (combatState as { attackCooldown: number }).attackCooldown = 0;
      (combatState as { isMoving: boolean }).isMoving = false;
      (combatState as { isDead: boolean }).isDead = false;

      // Reset visual state for all units (scale, opacity, visibility, health bar)
      if (mesh && this.unitRenderer) {
        const unitData = unit.getComponent(UnitData);
        const starLevel = unitData?.starLevel ?? 1;
        this.unitRenderer.showUnit(mesh.meshId, starLevel);
        this.unitRenderer.updateHealthBar(mesh.meshId, 1.0);
      }
    }
  }

  /**
   * Count survivors and their total health
   */
  private tallySurvivors(): SurvivorsTalliedEvent {
    const units = this.world
      .query(Position, Stats, Owner, CombatState)
      .filter((entity) => {
        const owner = entity.getComponent(Owner);
        return owner && !owner.isOnBench;
      });

    let player1Survivors = 0;
    let player2Survivors = 0;
    let player1TotalHealth = 0;
    let player2TotalHealth = 0;

    for (const unit of units) {
      const owner = unit.getComponent(Owner)!;
      const stats = unit.getComponent(Stats)!;
      const combatState = unit.getComponent(CombatState)!;

      if (combatState.isDead) continue;

      if (owner.playerId === 1) {
        player1Survivors++;
        player1TotalHealth += stats.currentHealth;
      } else {
        player2Survivors++;
        player2TotalHealth += stats.currentHealth;
      }
    }

    return {
      player1Survivors,
      player2Survivors,
      player1TotalHealth,
      player2TotalHealth,
    };
  }

  /**
   * Get unit counts by player
   */
  getUnitCounts(): { player1: number; player2: number } {
    const units = this.world
      .query(Position, Owner)
      .filter((entity) => {
        const owner = entity.getComponent(Owner);
        return owner && !owner.isOnBench;
      });

    let player1 = 0;
    let player2 = 0;

    for (const unit of units) {
      const owner = unit.getComponent(Owner)!;
      if (owner.playerId === 1) player1++;
      else player2++;
    }

    return { player1, player2 };
  }

  /**
   * Check if a player can place more units
   */
  canPlaceUnit(playerId: number, maxUnits: number): boolean {
    const counts = this.getUnitCounts();
    const current = playerId === 1 ? counts.player1 : counts.player2;
    return current < maxUnits;
  }

  /**
   * Proceed to next round (or end match)
   */
  proceedToNextRound(): void {
    this.gameState.proceedAfterRound();
  }

  /**
   * Get current game state info
   */
  getInfo(): {
    round: number;
    phase: GamePhase;
    scores: { player1: number; player2: number };
    timeRemaining: number;
  } {
    return {
      round: this.gameState.currentRound,
      phase: this.gameState.phase,
      scores: this.gameState.getScores(),
      timeRemaining: this.gameState.getRemainingTime(),
    };
  }
}
